import { Box, Button, TextField, Typography } from '@material-ui/core'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { AppState } from 'store/store'
import colors from 'utils/colors'
import useURLParams from 'utils/URLParamsContex'
import getEnvConfig from 'utils/getEnvConfig'

type WithdrawalFormState = {
    firstName: string
    lastName: string
    email: string
    contractIdentifier: string
    bookingID: string
    vzfID: string
    message: string
}

const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

const Withdrawal = (): JSX.Element => {
    const { t } = useTranslation()
    const envConfig = getEnvConfig()
    const { B2B } = useURLParams()
    const { contactData, orderID, vzfID } = useSelector((appState: AppState) => ({
        contactData: appState.contactData,
        orderID: appState.generalState.orderID,
        vzfID: appState.generalState.vzfID,
    }))
    const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle')
    const [formState, setFormState] = useState<WithdrawalFormState>({
        firstName: contactData.personalName ?? '',
        lastName: contactData.personalLastName ?? '',
        email: contactData.personalEmail ?? '',
        contractIdentifier: orderID || vzfID || '',
        bookingID: orderID || '',
        vzfID: vzfID || '',
        message: '',
    })

    const [loading, setLoading] = useState(false)

    const validationError = useMemo(() => {
        if (B2B) {
            return t('withdrawalStrings.b2bNotSupported')
        }
        if (formState.firstName.trim().length === 0 || formState.lastName.trim().length === 0) {
            return t('withdrawalStrings.requiredName')
        }
        if (!isEmail(formState.email.trim())) {
            return t('withdrawalStrings.invalidEmail')
        }
        if (formState.contractIdentifier.trim().length === 0) {
            return t('withdrawalStrings.requiredContract')
        }
        return ''
    }, [B2B, formState, t])

    const onChange = (field: keyof WithdrawalFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setFormState((prev) => ({
            ...prev,
            [field]: value,
        }))
    }

    const onSubmit = (): void => {
        setSubmitState('idle')
        if (validationError.length > 0 || loading) {
            return
        }
        setLoading(true)
        const withdrawalURL =
            process.env.NODE_ENV !== 'production'
                ? `http://${envConfig.Domain}:${envConfig.HTTPListen}/withdrawal`
                : '/withdrawal'
        fetch(withdrawalURL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                firstName: formState.firstName.trim(),
                lastName: formState.lastName.trim(),
                email: formState.email.trim(),
                contractIdentifier: formState.contractIdentifier.trim(),
                bookingID: formState.bookingID.trim(),
                vzfID: formState.vzfID.trim(),
                message: formState.message.trim(),
            }),
        })
            .then((res) => res.json())
            .then((res) => {
                if (res && res.status === 'success') {
                    setSubmitState('success')
                    return
                }
                setSubmitState('error')
            })
            .catch(() => setSubmitState('error'))
            .finally(() => setLoading(false))
    }

    return (
        <Box bgcolor={colors.white} display={'flex'} flexDirection={'column'} flex={1} p={4}>
            <Box maxWidth={720} width={1} mx={'auto'}>
                <Typography variant="h2">{t('withdrawalStrings.header')}</Typography>
                <Typography variant="body1" style={{ marginTop: 12, marginBottom: 24 }}>
                    {t('withdrawalStrings.subheader')}
                </Typography>

                <TextField
                    fullWidth
                    label={t('withdrawalStrings.firstName')}
                    value={formState.firstName}
                    onChange={onChange('firstName')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    fullWidth
                    label={t('withdrawalStrings.lastName')}
                    value={formState.lastName}
                    onChange={onChange('lastName')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    fullWidth
                    label={t('withdrawalStrings.email')}
                    value={formState.email}
                    onChange={onChange('email')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    fullWidth
                    label={t('withdrawalStrings.contractIdentifier')}
                    value={formState.contractIdentifier}
                    onChange={onChange('contractIdentifier')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    fullWidth
                    label={t('withdrawalStrings.messageOptional')}
                    value={formState.message}
                    onChange={onChange('message')}
                    margin="normal"
                    variant="outlined"
                    multiline
                    rows={4}
                />

                <Typography variant="body2" style={{ marginTop: 12 }}>
                    {t('withdrawalStrings.confirmationHint')}
                </Typography>

                {validationError.length > 0 && (
                    <Typography color="error" style={{ marginTop: 12 }}>
                        {validationError}
                    </Typography>
                )}
                {submitState === 'success' && (
                    <Typography style={{ color: colors.main, marginTop: 12 }}>
                        {t('withdrawalStrings.success')}
                    </Typography>
                )}
                {submitState === 'error' && (
                    <Typography color="error" style={{ marginTop: 12 }}>
                        {t('withdrawalStrings.error')}
                    </Typography>
                )}

                <Button
                    className={'FormButton NextButton'}
                    onClick={onSubmit}
                    disabled={validationError.length > 0 || loading}
                    style={{ marginTop: 24 }}
                >
                    {t('withdrawalStrings.submit')}
                </Button>
            </Box>
        </Box>
    )
}

export default Withdrawal
